import type { LogContext, Logger } from '@rock/types';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * The busy/toast/log wrapper every admin write hook runs its actions through: one action at a time,
 * failures surfaced as a toast and logged with the caller's context instead of rejecting into the
 * void. `run` resolves `true` only when the action completed, so callers can close a dialog (or move
 * on) on success without duplicating the try/catch.
 */
export function useBusyAction(log: Logger, context: LogContext = {}) {
    const [busy, setBusy] = useState(false);

    async function run(label: string, fn: () => Promise<void>): Promise<boolean> {
        setBusy(true);
        try {
            await fn();
            return true;
        } catch (err) {
            log.error(`Failed: ${label}`, { err, ...context });
            toast.error(`Failed: ${label}`, {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
            return false;
        } finally {
            setBusy(false);
        }
    }

    return { busy, run };
}
